import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { showSnackBar } from '../../../components/Snackbar/Snackbar'
import ModalComponent from '../../../components/Modal/Modal'
import Input from '../../../components/Input/Input'
import Calendar from '../../../components/Calendar/Calendar'
import { recordFinancing } from '../../../features/customer/customerSlice'
import { dateToServer } from '../../../utils/date'

// WS 2211251. The client paid in full through an outside lender. The server records the amount as a
// cleared manual Single Payment, stops the plan and pending payments, and marks the client financed.
// `defaults` (WS 2311820): "Mark paid" on a financing-pending client opens it with { amount, fundedAt }.
const RecordFinancingModal = ({ userId, open, onClose, onRecorded, defaults }) => {
  const dispatch = useDispatch()
  const [lender, setLender] = useState('')
  const [amount, setAmount] = useState('')
  const [fundedAt, setFundedAt] = useState(null)
  const [saving, setSaving] = useState(false)
  const [closeSignal, setCloseSignal] = useState(false)

  useEffect(() => {
    if (!open) return
    setLender('')
    setAmount(defaults?.amount ? String(defaults.amount) : '')
    setFundedAt(defaults?.fundedAt || null)
    setSaving(false)
  }, [open, defaults])

  const handleRecord = async () => {
    const value = Number(amount)
    const day = dateToServer(fundedAt)
    if (!lender.trim()) {
      showSnackBar('error', 'Enter the lender')
      return
    }
    if (!amount || !Number.isFinite(value) || value <= 0) {
      showSnackBar('error', 'Enter a valid amount')
      return
    }
    if (!day) {
      showSnackBar('error', 'Pick the funded date')
      return
    }
    setSaving(true)
    try {
      await dispatch(recordFinancing({ userId, body: { lender: lender.trim(), amount: value, fundedAt: day } })).unwrap()
      showSnackBar('success', 'Financing recorded')
      setCloseSignal((prev) => !prev)
      if (onRecorded) onRecorded()
    } catch (error) {
      showSnackBar('error', typeof error === 'string' ? error : 'Could not record financing')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalComponent
      title="Record Financing"
      customOpenModal={open}
      disableBtn={false}
      onClose={onClose}
      modalButtonTitle="Record"
      functionButton={handleRecord}
      disableCloseFunctionButton={true}
      triggerCloseModal={closeSignal}
      disabled={saving}
      modalWidth="min-w-[520px]"
    >
      <p className="text-sm text-text-secondary mb-4">
        The amount is recorded as paid (Single Payment, marked processed, nothing is charged). The plan and any
        pending payments stop, and the client shows "Paid in full – Financed". Failed payments stay in Remaining Balance.
      </p>
      <Input
        placeholder="Lender name"
        label="Lender"
        maxLength={200}
        value={lender}
        onChange={(e) => setLender(e.target.value)}
      />
      <Input
        placeholder="Financed amount"
        parentClassName="mt-6"
        type="Number"
        label="Amount"
        leftSideIcon={<div className="text-text-secondary">$</div>}
        leftSideIconStyle="ml-5 mt-[17px]"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <Calendar
        parentClassName="mt-6"
        width="w-full"
        label="Funded On"
        value={fundedAt}
        onChange={(val) => setFundedAt(val)}
        isRequired={true}
        timezone="system"
      />
    </ModalComponent>
  )
}

export default RecordFinancingModal
